import { MonoTypeOperatorFunction } from 'rxjs/index';
import { filter } from 'rxjs/operators';

//  ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//              TYPES
//  ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
interface Type {
    readonly type: string;
}

interface Payload<T> {
    readonly payload: T;
}

type ActionType<T> = Type & Payload<T>;

type ActionCreator<T> = (payload: T) => ActionType<T>;

type TypedActionCreator<T> = Type & ActionCreator<T>;

type BoundActionCreator<T> = (payload: T) => void;

//  ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//              FACTORY FUNCTIONS
//  ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
function createType(type: string): Type {
    return {type};
}

function createPayload<T>(payload: T): Payload<T> {
    return {payload};
}

function createAction<T>(type: Type, payload: Payload<T>): ActionType<T> {
    return {
        type: type.type,
        payload: payload.payload
    };
}

function createActionCreator<T>(type: Type): TypedActionCreator<T> {
    const actionCreator = (payload: T) => createAction(type, createPayload(payload));
    (actionCreator as any).type = type.type;

    return actionCreator as TypedActionCreator<T>;
}

function createBoundActionCreator<T>(actionCreator: ActionCreator<T>, dispatchFn: (action: ActionType<T>) => void): BoundActionCreator<T> {
    return (payload: T) => {
        const action = actionCreator(payload);
        dispatchFn(action);
    };
}

//  ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
//              TYPE GUARDS
//  ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
function isType<T>(action: ActionType<any>, ...expectedAction: Array<ActionType<T>>): action is ActionType<T> {
    return expectedAction.some(e => e.type === action.type);
}

function isAction<T>(action: ActionType<any>, ...expectedActionCreator: Array<TypedActionCreator<T>>): action is ActionType<T> {
    return expectedActionCreator.some(e => e.type === action.type);
}

// Custom RXJS Operator
function selectAction<T extends ActionType<F>, F>(...actionCreators: Array<TypedActionCreator<F>>):
    MonoTypeOperatorFunction<ActionType<F>> {
    return filter((action: ActionType<F>): action is T => {
        return actionCreators.some(fn => fn.type === action.type);
    });
}

export {
    Type, Payload, ActionType,
    TypedActionCreator,
    createType,
    createPayload,
    createAction,
    createActionCreator,
    createBoundActionCreator,
    isType,
    isAction,
    selectAction
};
